import React, { useState, useRef, useEffect } from "react";
import { Search, Download, Eye, X } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import {
  useGetUserByEmailQuery,
  useGetUsersQuery,
} from "../../slices/userApiSlice";
import {
  useGetMaintenanceReportsQuery,
  useGetMaintenanceRequestQuery,
  useUpdatePreventiveMaintenanceMutation,
} from "../../slices/maintenanceApiSlice";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const Maintenancereport = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [editableData, setEditableData] = useState({
    Additional_cost: "",
    Additional_Hour: "",
    Remarks: "",
  });

  const rowsPerPage = 10;

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || "",
  );
  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  // Use the academy ID from the user info to fetch filtered reports
  // If user info is not loaded yet, skip the query
  const loginAcademyId = userByEmial?.user?.academyId?.trim();

  const { data: maintenanceReport, isLoading: isReportsLoading } =
    useGetMaintenanceReportsQuery(loginAcademyId, {
      skip: !loginAcademyId, // Skip query until we have the academy ID
    });

  const { data: maintenanceRequest, isLoading: isRequestsLoading } =
    useGetMaintenanceRequestQuery();

  const { data: users } = useGetUsersQuery();

  const isLoading = isReportsLoading || isRequestsLoading;

  const [updatePreventiveMaintenance, { isLoading: isUpdating }] =
    useUpdatePreventiveMaintenanceMutation();

  // Optimization: Use useMemo to compute derived data and avoid O(N*M) complexity
  const data = React.useMemo(() => {
    // We only need the maintenance reports now, as the backend populates
    // userName, userEmail, and assetName directly in the report objects!
    if (!maintenanceReport || !loginAcademyId) {
      return [];
    }

    // Process reports directly from the backend response
    // The backend now provides: assetName, userName, userEmail, description, area, totalTechnicians
    const processed = maintenanceReport
      .map((report) => {
        // Return a merged object
        return {
          ...report,
          // Map backend fields to UI expectations if they differ
          name: report.userName,
          email: report.userEmail,
          assetCode: report.assetCode, // Explicitly mapping assetCode for clarity
          // Ensure consistent casing for sensitive properties used in sorting or export
          Area: report.area, // Maps 'area' to 'Area' for code paths expecting capitalized key
          // Note: 'description' and 'totalTechnicians' are likely used as-is
        };
      })
      .sort((a, b) => new Date(b.finishedDate) - new Date(a.finishedDate));

    return processed;

    // We no longer depend on 'maintenanceRequest' or 'users' for the basic list
  }, [maintenanceReport, loginAcademyId]);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading reports...", { id: "loading-reports" });
    } else {
      toast.dismiss("loading-reports");
    }
  }, [isLoading]);

  const handleUpdate = async () => {
    if (modalData && editableData) {
      if (!editableData.Additional_cost || !editableData.Additional_Hour) {
        toast.warning("Missing Fields", {
          description: "Please fill in all required fields.",
        });
        return;
      }

      // Since we fetch straight from backend now, 'maintenance.preventiveMaintenanceID' is the link back
      const maintenanceID = modalData.preventiveMaintenanceID;

      const maintenanceUpdate = {
        updatedData: {
          partsUsed: editableData.Remarks,
          labourCost: parseFloat(editableData.Additional_cost),
          totalCost:
            parseFloat(editableData.Additional_cost) +
            (modalData.totalCost - modalData.labourCost), // Use modalData directly
          reportStatus: "Approved",
          completionTime: `${editableData.Additional_Hour} hours`,
        },
        id: maintenanceID,
      };

      try {
        await updatePreventiveMaintenance(maintenanceUpdate).unwrap();
        toast.success("Success", {
          description: "Maintenance report updated successfully.",
        });
        setModalData(null);
      } catch (err) {
        toast.error("Error", {
          description: "Failed to update maintenance report.",
        });
        console.error("Failed to update maintenance report:", err);
      }
    }
  };

  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) return;

    const selectedData = data.filter((item) =>
      selectedRows.includes(item.maintenanceReportID),
    );

    const csvContent = [
      [
        "Repair ID",
        "Asset Code",
        "Asset Name",
        "Start Time",
        "End Time",
        "Date",
        "Area",
        "Total Cost",
        "Parts Used",
        "Description",
        "Total Technicians",
        "Assigned Technician",
      ],
      ...selectedData.map((item) => [
        item.maintenanceReportID,
        item.assetCode,
        item.assetName,
        item.startTime,
        item.endTime,
        item.Date,
        item.area || item.Area, // Fallback to avoid issues
        item.totalCost,
        item.partsUsed,
        item.description, // Added description to CSV
        item.totalTechnicians, // Added total technicians to CSV
        item.technicians,
      ]),
    ]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "maintenance_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const sortedData = data;

  const filteredData = sortedData.filter((item) => {
    const matchesSearch = Object.values(item).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  console.log("Displayed Data:", displayedData);

  const handleSelectRow = (maintenanceReportID) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(maintenanceReportID)
        ? prevSelectedRows.filter((item) => item !== maintenanceReportID)
        : [...prevSelectedRows, maintenanceReportID],
    );
  };

  const handleView = (item) => {
    setModalData(item);
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDownloadPDF = () => {
    if (!modalData) return;

    const doc = new jsPDF();
    doc.text("Maintenance Report", 14, 15);

    const columns = ["Field", "Value"];
    const rows = [
      ["RID", modalData.maintenanceReportID],
      ["Asset Code", modalData.assetCode],
      ["Asset Name", modalData.assetName],
      ["Start Time", modalData.startTime],
      ["End Time", modalData.endTime],
      ["Date", modalData.finishedDate],
      ["Area", modalData.area || modalData.Area],
      ["Total Cost", modalData.totalCost],
      ["Part Used", modalData.partsUsed],
      ["Description", modalData.description],
      ["Total Technicians", modalData.totalTechnicians],
      ["Assigned Technician", modalData.technicians],
      ["Additional Info", modalData.information],
    ];

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 20,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`Maintenance_Report_${modalData.maintenanceReportID}.pdf`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Maintenance Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and analyze preventive maintenance reports and statistics
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search maintenance reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button
            onClick={handleDownloadSelected}
            disabled={selectedRows.length === 0}
            variant="default"
            style={{
              backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Download Selected
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={
                      selectedRows.length === displayedData.length &&
                      displayedData.length > 0
                    }
                    onCheckedChange={() =>
                      setSelectedRows(
                        selectedRows.length === displayedData.length
                          ? []
                          : displayedData.map(
                              (item) => item.maintenanceReportID,
                            ),
                      )
                    }
                  />
                </TableHead>
                <TableHead className="font-semibold">Sl. No.</TableHead>
                <TableHead className="font-semibold">Asset Code</TableHead>
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Time</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Area</TableHead>
                <TableHead className="font-semibold">Total Cost</TableHead>
                <TableHead className="font-semibold">Parts Used</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow key={item.maintenanceReportID || index}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.includes(
                          item.maintenanceReportID,
                        )}
                        onCheckedChange={() =>
                          handleSelectRow(item.maintenanceReportID)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{item.assetCode || "-"}</TableCell>
                    <TableCell>{item.assetName || "-"}</TableCell>
                    <TableCell>
                      {[item.startTime, item.endTime].join(" - ")}
                    </TableCell>
                    <TableCell>{formatDate(item.finishedDate)}</TableCell>
                    <TableCell>{item.area || "-"}</TableCell>
                    <TableCell>{item.totalCost || "-"}</TableCell>
                    <TableCell>{item.partsUsed || "-"}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {item.description || "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(item)}
                        style={{
                          backgroundColor:
                            "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                          color: "white",
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No maintenance reports found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="border-t bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing{" "}
            <span className="font-medium text-foreground">
              {displayedData.length > 0
                ? (currentPage - 1) * rowsPerPage + 1
                : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium text-foreground">
              {Math.min(currentPage * rowsPerPage, filteredData.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-foreground">
              {filteredData.length}
            </span>{" "}
            results
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Dialog
        open={!!modalData}
        onOpenChange={(open) => !open && handleCloseModal()}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Maintenance Report Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset Name</Label>
                <Input value={modalData?.assetName || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Asset Code</Label>
                <Input value={modalData?.assetCode || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  value={formatDate(modalData?.finishedDate) || ""}
                  readOnly
                />
              </div>

              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input value={modalData?.startTime || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>End Time</Label>
                <Input value={modalData?.endTime || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Area</Label>
                <Input value={modalData?.area || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Total Cost</Label>
                <Input value={modalData?.totalCost || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Parts Used</Label>
                <Input value={modalData?.partsUsed || ""} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Total Technicians</Label>
                <Input value={modalData?.totalTechnicians || ""} readOnly />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Assigned Technicians</Label>
              <Input value={modalData?.technicians || ""} readOnly />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={modalData?.description || ""}
                readOnly
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Additional Information</Label>
              <Textarea
                value={modalData?.information || ""}
                readOnly
                rows={2}
              />
            </div>

            {modalData?.images && (
              <div className="space-y-2">
                <Label>Maintenance Images</Label>
                <div className="grid grid-cols-2 gap-4">
                  {Array.isArray(modalData.images) &&
                  modalData.images.length > 0 ? (
                    modalData.images.map((imgSrc, index) => (
                      <img
                        key={index}
                        src={imgSrc}
                        alt={`Maintenance ${index + 1}`}
                        className="w-full h-48 object-cover rounded border"
                      />
                    ))
                  ) : modalData.images ? (
                    <img
                      src={modalData.images}
                      alt="Maintenance"
                      className="w-full h-48 object-cover rounded border"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No image available
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Additional Cost</Label>
                {modalData?.addCost ? (
                  <Input value={modalData.addCost} readOnly />
                ) : (
                  <Input
                    name="Additional_cost"
                    value={editableData.Additional_cost}
                    onChange={handleInputChange}
                    type="number"
                    placeholder="Enter additional cost"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Additional Hours</Label>
                {modalData?.addHours ? (
                  <Input value={modalData.addHours} readOnly />
                ) : (
                  <Input
                    name="Additional_Hour"
                    value={editableData.Additional_Hour}
                    onChange={handleInputChange}
                    type="number"
                    placeholder="Enter additional hours"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              {modalData?.remark ? (
                <Textarea value={modalData.remark} readOnly rows={2} />
              ) : (
                <Textarea
                  name="Remarks"
                  value={editableData.Remarks}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Enter remarks"
                />
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {!modalData?.addCost &&
              !modalData?.addHours &&
              !modalData?.remark && (
                <Button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  style={{
                    backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  }}
                >
                  {isUpdating ? "Updating..." : "Update"}
                </Button>
              )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Maintenancereport;
